/**
 * Shared AOI insight tabs (overview / impact / remote / optional history & actions)
 * for the map popup and SAR result modal.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';

import {
  Tabs,
  Badge,
  Timeline,
  Image,
  Button,
  Spin,
  Empty,
  Select,
  Tooltip,
  Modal,
  Switch,
} from 'antd';
import type { Feature, Geometry } from 'geojson';
import type { TFunction } from 'i18next';
import {
  MapPin,
  Clock,
  Phone,
  Calendar,
  ShieldAlert,
  History,
  LayoutGrid,
  Building2,
  Satellite,
  Download,
  AlertCircle,
  RotateCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import {
  ALL_GALLERY_FETCH_SPECS,
  fetchSarGalleryFrame,
  galleryFetchSpecFromUi,
  gallerySpecsEqual,
  geeFrameKeyFromFrame,
  geeFrameKeyFromSpec,
} from '@/features/analytics/utils/sarResultAlphaEarthGallery';
import { LandCoverPercentBarRow } from '@/features/map/components/ui/LandCoverPercentBarRow';
import { RemoteSceneColumnTimeScrubber } from '@/features/map/components/ui/RemoteSceneColumnTimeScrubber';
import { RemoteSceneGeoreferencedPreview } from '@/features/map/components/ui/RemoteSceneGeoreferencedPreview';
import { SarAoiLandCoverMapTab } from '@/features/map/components/ui/SarAoiLandCoverMapTab';
import { useDebouncedValue } from '@/features/map/hooks/useDebouncedValue';
import { SAR_SCENE_GALLERY_INDICES } from '@/features/map/types/map.types';
import type { AlphaEarthIndex } from '@/features/map/types/map.types';
import { resolveAlphaEarthBandsParam } from '@/features/map/utils/alphaEarthApi';
import { getPalettePresetsForIndex } from '@/features/map/utils/alphaEarthIndexPalettePresets';
import {
  fetchAlphaEarthSarPreviewTileUrl,
  resolveSarPreviewBboxAndTileXY,
} from '@/features/map/utils/alphaEarthSarPreviewTile';
import { computeLandCoverDistribution } from '@/features/map/utils/mockLandCoverFeaturesInAoi';
import {
  isoDateFromMs,
  parseRemoteWindow,
  scrubPreviewDepthMeters,
  t01FromDateMs,
} from '@/features/map/utils/sarRemoteScrubUtils';
import { useTemporalDisplayContext } from '@/features/scenario/hooks/useTemporalDisplayContext';
import type {
  AoiLandCoverCategoryKey,
  AoiRemoteGeeFrame,
  AoiRemoteRgbProfile,
  AoiRemoteSceneSlide,
  EnhancedFloodPoint,
} from '@/types/flood.types';

export interface EnhancedFloodPointInsightPanelProps {
  floodPoint: EnhancedFloodPoint;
  /** Scrollable tab body max-height (CSS length) */
  contentMaxHeight?: string;
  /** Hide history tab (e.g. SAR job modal) */
  hideHistoryTab?: boolean;
  /** Hide emergency contacts tab */
  hideActionsTab?: boolean;
  showLastUpdatedFooter?: boolean;
  /** Giới hạn ngày xem lớp phủ (ISO), truyền từ tham số job SAR */
  landCoverDateRange?: { minIso: string; maxIso: string };
  /** Kết quả job chỉ lớp phủ — render thẳng panel lớp phủ, không có tabs flood. */
  landCoverOnly?: boolean;
  /**
   * Phóng vùng xem so sánh ảnh (ví dụ modal kết quả job) — cao hơn popup map mặc định.
   */
  spaciousRemoteGallery?: boolean;
}

function StatBox({
  icon,
  label,
  value,
  labelTooltip,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  /** Giải thích ngắn khi hover (gạch chấm gợi ý có tooltip). */
  labelTooltip?: string;
}) {
  const labelEl =
    labelTooltip != null && labelTooltip.trim() !== '' ? (
      <Tooltip title={labelTooltip} placement="topLeft">
        <span className="cursor-help border-b border-dotted border-slate-500/45 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
          {label}
        </span>
      </Tooltip>
    ) : (
      <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
        {label}
      </span>
    );

  return (
    <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] p-3">
      <div className="mb-1 flex items-center gap-2">
        {icon}
        {labelEl}
      </div>
      <div className="text-base font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {value}
      </div>
    </div>
  );
}

type TabKey = 'overview' | 'impact' | 'remote' | 'history' | 'actions';

function remoteGeeFramesForSlide(
  slide: AoiRemoteSceneSlide,
  lazyBySlide: Record<string, AoiRemoteGeeFrame[]>,
): AoiRemoteGeeFrame[] {
  const fromApi = slide.geeFrames?.filter((f) => f.imageUrl?.trim()) ?? [];
  const sk = getRemoteSceneSlideKey(slide);
  const extra = lazyBySlide[sk] ?? [];
  const map = new Map<string, AoiRemoteGeeFrame>();
  for (const f of fromApi) {
    map.set(geeFrameKeyFromFrame(f), f);
  }
  for (const f of extra) {
    if (f.imageUrl?.trim()) map.set(geeFrameKeyFromFrame(f), f);
  }
  const merged = Array.from(map.values());
  if (merged.length > 0) return merged;
  const legacy = slide.imageUrl?.trim();
  if (legacy)
    return [{ index: 'RGB', rgbProfile: 'sentinel2', imageUrl: legacy, caption: slide.caption }];
  return [];
}

function pickRemoteFrame(
  frames: AoiRemoteGeeFrame[],
  index: AlphaEarthIndex,
  rgbProfile: AoiRemoteRgbProfile,
): AoiRemoteGeeFrame | undefined {
  const withUrl = frames.filter((f) => f.imageUrl?.trim());
  if (withUrl.length === 0) return undefined;
  if (index !== 'RGB') {
    return withUrl.find((f) => f.index === index);
  }
  const rgb = withUrl.filter((f) => f.index === 'RGB');
  const chosen = rgb.find((f) => (f.rgbProfile ?? 'sentinel2') === rgbProfile);
  return chosen ?? rgb[0];
}

/** Nhãn cột so sánh ảnh — trung tính (không chỉ phân tích lũ); cùng khóa với tóm tắt bước tham số wizard. */
function remoteColumnRoleShortLabel(t: TFunction, role: 'baseline' | 'crisis'): string {
  return role === 'baseline' ? t('map.sarAoi.paramRowReference') : t('map.sarAoi.paramRowCompare');
}

/** BCP 47 tag for `Intl` from i18next language (vi → vi-VN). */
function displayLocaleTag(lang: string | undefined): string {
  const l = (lang ?? 'en').toLowerCase();
  if (l.startsWith('vi')) return 'vi-VN';
  return 'en-US';
}

function landCoverLabelKey(k: AoiLandCoverCategoryKey): string {
  return `flood.aoiInsight.landCover.${k}`;
}

/** Khóa ổn định cho một slide gallery (baseline/crisis + ngày + nhãn kỳ). */
function getRemoteSceneSlideKey(slide: AoiRemoteSceneSlide): string {
  return `${slide.role}:${slide.referenceDate ?? ''}:${slide.periodLabel}`;
}

function sortRemoteSlidesChronologically(slides: AoiRemoteSceneSlide[]): AoiRemoteSceneSlide[] {
  return [...slides].sort((a, b) => {
    const da = a.referenceDate ?? '';
    const db = b.referenceDate ?? '';
    const byDate = da.localeCompare(db);
    if (byDate !== 0) return byDate;
    return a.periodLabel.localeCompare(b.periodLabel);
  });
}

function EnhancedFloodPointInsightPanelInner({
  floodPoint,
  contentMaxHeight = 'min(52vh,340px)',
  hideHistoryTab = false,
  hideActionsTab = false,
  showLastUpdatedFooter = true,
  landCoverDateRange,
  landCoverOnly = false,
  spaciousRemoteGallery = false,
}: EnhancedFloodPointInsightPanelProps) {
  const { t, i18n } = useTranslation();
  const localeTag = displayLocaleTag(i18n.resolvedLanguage ?? i18n.language);

  const formatTime = useCallback(
    (isoString: string) =>
      new Date(isoString).toLocaleTimeString(localeTag, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [localeTag],
  );

  const formatDate = useCallback(
    (isoString: string) =>
      new Date(isoString).toLocaleDateString(localeTag, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [localeTag],
  );

  /** Hiển thị ngày từ `YYYY-MM-DD` (tránh lệch múi khi dùng trưa local). */
  const formatDateYmd = useCallback((ymd: string) => formatDate(`${ymd}T12:00:00`), [formatDate]);

  const handleExportGeoJson = useCallback(() => {
    const geometry: Geometry = floodPoint.exportGeometry ?? {
      type: 'Point',
      coordinates: floodPoint.position,
    };
    const feature: Feature = {
      type: 'Feature',
      geometry,
      properties: {
        id: floodPoint.id,
        name: floodPoint.name,
        riskLevel: floodPoint.riskLevel,
        confidence: floodPoint.confidence,
        lastUpdated: floodPoint.lastUpdated,
        areaKm2: floodPoint.areaKm2,
      },
    };
    const blob = new Blob([JSON.stringify(feature, null, 2)], {
      type: 'application/geo+json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aoi-${floodPoint.id.replace(/[^\w-]+/g, '_')}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }, [floodPoint]);

  const { allowsHistoryTimeline } = useTemporalDisplayContext();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  const [remoteSpectralIndex, setRemoteSpectralIndex] = useState<AlphaEarthIndex>('RGB');
  const remoteRgbProfile: AoiRemoteRgbProfile = 'embedding';
  const [remotePaletteCsv, setRemotePaletteCsv] = useState('');
  const [remotePaletteTileUrls, setRemotePaletteTileUrls] = useState<Record<string, string>>({});
  const [remotePaletteLoading, setRemotePaletteLoading] = useState(false);
  /** Per-slide palette status: loading/error (key = `${slide.role}-${slide.referenceDate}`). */
  const [remotePaletteStatus, setRemotePaletteStatus] = useState<
    Record<string, 'loading' | 'error'>
  >({});
  const [remoteShowSatelliteRaster, setRemoteShowSatelliteRaster] = useState(false);
  const [remotePreviewModal, setRemotePreviewModal] = useState<{
    role: 'baseline' | 'crisis';
    title: string;
    imageUrl: string;
  } | null>(null);
  /** Frame gallery lazy-fetch (bổ sung sau hydrate tối thiểu RGB S2). */
  const [lazyFramesBySlide, setLazyFramesBySlide] = useState<Record<string, AoiRemoteGeeFrame[]>>(
    {},
  );
  /** Spec đang fetch cho từng slide (slideKey → set of specKey). */
  const [inflightSpecsBySlide, setInflightSpecsBySlide] = useState<Record<string, Set<string>>>({});
  /** Spec đã fail (không có tile / fallback cũng trống). */
  const [failedSpecsBySlide, setFailedSpecsBySlide] = useState<Record<string, Set<string>>>({});
  /** Token retry để kích lại useEffect lazy-fetch khi user bấm "Thử lại". */
  const [lazyRetryToken, setLazyRetryToken] = useState(0);
  const [paletteRetryToken, setPaletteRetryToken] = useState(0);
  const remoteIdlePrefetchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    remoteIdlePrefetchKeyRef.current = null;
    queueMicrotask(() => {
      setLazyFramesBySlide({});
      setInflightSpecsBySlide({});
      setFailedSpecsBySlide({});
      setRemotePaletteStatus({});
      setRemoteShowSatelliteRaster(false);
    });
  }, [floodPoint.id]);

  const handleRemoteSpectralIndexChange = useCallback((v: AlphaEarthIndex) => {
    setRemoteSpectralIndex(v);
    if (v === 'RGB') {
      setRemotePaletteCsv('');
    } else {
      const first = getPalettePresetsForIndex(v)[0]?.palette ?? '';
      setRemotePaletteCsv(first);
    }
  }, []);

  const showHistoryTab = !hideHistoryTab && allowsHistoryTimeline;

  const effectiveTab: TabKey =
    !showHistoryTab && activeTab === 'history'
      ? 'overview'
      : hideActionsTab && activeTab === 'actions'
        ? 'overview'
        : activeTab;

  const ctx = floodPoint.aoiContext;

  const landCoverDistributionRows = useMemo(
    () => computeLandCoverDistribution(ctx?.landCoverImpacts ?? []),
    [ctx?.landCoverImpacts],
  );

  const baselineSlidesSorted = useMemo((): AoiRemoteSceneSlide[] => {
    const slides = ctx?.remoteSceneSlides?.filter((s) => s.role === 'baseline') ?? [];
    return sortRemoteSlidesChronologically(slides);
  }, [ctx?.remoteSceneSlides]);

  const crisisSlidesSorted = useMemo((): AoiRemoteSceneSlide[] => {
    const slides = ctx?.remoteSceneSlides?.filter((s) => s.role === 'crisis') ?? [];
    return sortRemoteSlidesChronologically(slides);
  }, [ctx?.remoteSceneSlides]);

  const resolvedBaselineSlide = useMemo((): AoiRemoteSceneSlide | null => {
    if (baselineSlidesSorted.length === 0) return null;
    return baselineSlidesSorted[0] ?? null;
  }, [baselineSlidesSorted]);

  const resolvedCrisisSlide = useMemo((): AoiRemoteSceneSlide | null => {
    if (crisisSlidesSorted.length === 0) return null;
    return crisisSlidesSorted[0] ?? null;
  }, [crisisSlidesSorted]);

  const enableScrub = useMemo(
    () => Boolean(ctx?.remoteTimeWindows && floodPoint.exportGeometry && !landCoverOnly),
    [ctx?.remoteTimeWindows, floodPoint.exportGeometry, landCoverOnly],
  );

  const [scrubBaselineIso, setScrubBaselineIso] = useState<string | null>(null);
  const [scrubCrisisIso, setScrubCrisisIso] = useState<string | null>(null);
  /** 0..1 theo mốc trên dòng thời gian — tách khỏi chuỗi ngày (cùng ngày vẫn đổi mô phỏng theo vị trí kéo). */
  const [scrubBaselineT01, setScrubBaselineT01] = useState(0.5);
  const [scrubCrisisT01, setScrubCrisisT01] = useState(0.5);

  useEffect(() => {
    const remote = ctx?.remoteTimeWindows;
    const slidesRef = ctx?.remoteSceneSlides;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      if (!enableScrub || !remote) {
        setScrubBaselineIso(null);
        setScrubCrisisIso(null);
        return;
      }
      const wb = parseRemoteWindow(remote.baseline);
      const wc = parseRemoteWindow(remote.crisis);
      if (!wb || !wc) {
        setScrubBaselineIso(null);
        setScrubCrisisIso(null);
        return;
      }
      const slidesB = sortRemoteSlidesChronologically(
        slidesRef?.filter((s) => s.role === 'baseline') ?? [],
      );
      const slidesC = sortRemoteSlidesChronologically(
        slidesRef?.filter((s) => s.role === 'crisis') ?? [],
      );
      const b0 = slidesB[0]?.referenceDate ?? isoDateFromMs(wb.startMs);
      const c0 = slidesC[0]?.referenceDate ?? isoDateFromMs(wc.startMs);
      setScrubBaselineIso(b0);
      setScrubCrisisIso(c0);
      setScrubBaselineT01(t01FromDateMs(wb, new Date(`${b0}T12:00:00`).getTime()));
      setScrubCrisisT01(t01FromDateMs(wc, new Date(`${c0}T12:00:00`).getTime()));
    });
    return () => {
      cancelled = true;
    };
  }, [floodPoint.id, enableScrub, ctx?.remoteTimeWindows, ctx?.remoteSceneSlides]);

  const debouncedBaselineIso = useDebouncedValue(scrubBaselineIso, 280);
  const debouncedCrisisIso = useDebouncedValue(scrubCrisisIso, 280);

  const scrubReady = Boolean(scrubBaselineIso && scrubCrisisIso);

  const displayBaselineSlide = useMemo((): AoiRemoteSceneSlide | null => {
    if (!enableScrub || !scrubBaselineIso) return null;
    const tmpl = resolvedBaselineSlide;
    return {
      role: 'baseline',
      referenceDate: scrubBaselineIso,
      periodLabel: formatDateYmd(scrubBaselineIso),
      imageUrl: tmpl?.imageUrl,
      caption: tmpl?.caption,
      geeFrames: tmpl?.geeFrames,
    };
  }, [enableScrub, scrubBaselineIso, resolvedBaselineSlide, formatDateYmd]);

  const displayCrisisSlide = useMemo((): AoiRemoteSceneSlide | null => {
    if (!enableScrub || !scrubCrisisIso) return null;
    const tmpl = resolvedCrisisSlide;
    return {
      role: 'crisis',
      referenceDate: scrubCrisisIso,
      periodLabel: formatDateYmd(scrubCrisisIso),
      imageUrl: tmpl?.imageUrl,
      caption: tmpl?.caption,
      geeFrames: tmpl?.geeFrames,
    };
  }, [enableScrub, scrubCrisisIso, resolvedCrisisSlide, formatDateYmd]);

  const fetchBaselineSlide = useMemo((): AoiRemoteSceneSlide | null => {
    if (!enableScrub) return null;
    const iso = debouncedBaselineIso ?? scrubBaselineIso;
    if (!iso) return null;
    const tmpl = resolvedBaselineSlide;
    return {
      role: 'baseline',
      referenceDate: iso,
      periodLabel: formatDateYmd(iso),
      imageUrl: tmpl?.imageUrl,
      caption: tmpl?.caption,
      geeFrames: tmpl?.geeFrames,
    };
  }, [enableScrub, debouncedBaselineIso, scrubBaselineIso, resolvedBaselineSlide, formatDateYmd]);

  const fetchCrisisSlide = useMemo((): AoiRemoteSceneSlide | null => {
    if (!enableScrub) return null;
    const iso = debouncedCrisisIso ?? scrubCrisisIso;
    if (!iso) return null;
    const tmpl = resolvedCrisisSlide;
    return {
      role: 'crisis',
      referenceDate: iso,
      periodLabel: formatDateYmd(iso),
      imageUrl: tmpl?.imageUrl,
      caption: tmpl?.caption,
      geeFrames: tmpl?.geeFrames,
    };
  }, [enableScrub, debouncedCrisisIso, scrubCrisisIso, resolvedCrisisSlide, formatDateYmd]);

  const remoteSceneSlidesForFetch = useMemo((): AoiRemoteSceneSlide[] | undefined => {
    if (!enableScrub || !fetchBaselineSlide || !fetchCrisisSlide) {
      return ctx?.remoteSceneSlides;
    }
    const rest = (ctx?.remoteSceneSlides ?? []).filter(
      (s) => s.role !== 'baseline' && s.role !== 'crisis',
    );
    return [fetchBaselineSlide, fetchCrisisSlide, ...rest];
  }, [enableScrub, fetchBaselineSlide, fetchCrisisSlide, ctx?.remoteSceneSlides]);

  /** Không dùng polygon giả — chờ GeoJSON từ job/API khi có. */
  const scrubCrisisFloodGeometry = null;
  const scrubBaselineFloodGeometry = null;

  const remoteScenePreviewColumns = useMemo(() => {
    type Col = {
      role: 'baseline' | 'crisis';
      displaySlide: AoiRemoteSceneSlide;
      fetchSlide: AoiRemoteSceneSlide;
    };
    const out: Col[] = [];
    if (
      enableScrub &&
      scrubReady &&
      fetchBaselineSlide &&
      fetchCrisisSlide &&
      displayBaselineSlide &&
      displayCrisisSlide
    ) {
      out.push(
        { role: 'baseline', displaySlide: displayBaselineSlide, fetchSlide: fetchBaselineSlide },
        { role: 'crisis', displaySlide: displayCrisisSlide, fetchSlide: fetchCrisisSlide },
      );
    } else {
      if (resolvedBaselineSlide) {
        out.push({
          role: 'baseline',
          displaySlide: resolvedBaselineSlide,
          fetchSlide: resolvedBaselineSlide,
        });
      }
      if (resolvedCrisisSlide) {
        out.push({
          role: 'crisis',
          displaySlide: resolvedCrisisSlide,
          fetchSlide: resolvedCrisisSlide,
        });
      }
    }
    return out;
  }, [
    enableScrub,
    scrubReady,
    fetchBaselineSlide,
    fetchCrisisSlide,
    displayBaselineSlide,
    displayCrisisSlide,
    resolvedBaselineSlide,
    resolvedCrisisSlide,
  ]);

  const defaultSpectralPalette = useMemo(() => {
    if (remoteSpectralIndex === 'RGB') return '';
    return getPalettePresetsForIndex(remoteSpectralIndex)[0]?.palette ?? '';
  }, [remoteSpectralIndex]);

  const spectralPalettePresets = useMemo(
    () => (remoteSpectralIndex === 'RGB' ? [] : getPalettePresetsForIndex(remoteSpectralIndex)),
    [remoteSpectralIndex],
  );

  /** Chuỗi ổn định theo tọa độ — tránh Polygon reference mới mỗi lần parent build lại `floodPoint`. */
  const alphaEarthPreviewGeometryKey =
    floodPoint.exportGeometry?.coordinates != null
      ? JSON.stringify(floodPoint.exportGeometry.coordinates)
      : '';

  const sarPreviewTileXY = useMemo(
    () => resolveSarPreviewBboxAndTileXY(floodPoint.exportGeometry),
    [floodPoint.exportGeometry],
  );

  useEffect(() => {
    const clearPaletteTiles = () => {
      queueMicrotask(() => {
        setRemotePaletteTileUrls({});
        setRemotePaletteLoading(false);
      });
    };

    if (remoteSpectralIndex === 'RGB') {
      clearPaletteTiles();
      return;
    }
    if (!remoteShowSatelliteRaster) {
      clearPaletteTiles();
      return;
    }
    const geom = floodPoint.exportGeometry;
    const slides = remoteSceneSlidesForFetch;
    if (!env.SAR_RESULT_ALPHAEARTH_PREVIEW || !geom || !slides?.length) {
      clearPaletteTiles();
      return;
    }

    if (remotePaletteCsv === defaultSpectralPalette) {
      clearPaletteTiles();
      return;
    }

    const tile = resolveSarPreviewBboxAndTileXY(geom);
    if (!tile) {
      clearPaletteTiles();
      return;
    }

    let cancelled = false;
    const bands = resolveAlphaEarthBandsParam(remoteSpectralIndex, null);

    queueMicrotask(() => {
      if (cancelled) return;
      setRemotePaletteLoading(true);
      void (async () => {
        const datedSlides = slides.filter((s): s is typeof s & { referenceDate: string } =>
          Boolean(s.referenceDate?.trim()),
        );
        setRemotePaletteStatus(() => {
          const init: Record<string, 'loading' | 'error'> = {};
          for (const s of datedSlides) init[`${s.role}-${s.referenceDate}`] = 'loading';
          return init;
        });
        const resolved = await Promise.all(
          datedSlides.map(async (slide) => {
            const pk = `${slide.role}-${slide.referenceDate}`;
            const url = await fetchAlphaEarthSarPreviewTileUrl({
              bbox: tile.bbox,
              date: slide.referenceDate,
              index: remoteSpectralIndex,
              bands,
              palette: remotePaletteCsv,
              x: tile.x,
              y: tile.y,
            });
            if (cancelled) return null;
            setRemotePaletteStatus((prev) => {
              const next = { ...prev };
              if (url?.trim()) delete next[pk];
              else next[pk] = 'error';
              return next;
            });
            return url ? ([pk, url] as const) : null;
          }),
        );
        if (cancelled) return;
        const next = Object.fromEntries(
          resolved.filter((e): e is readonly [string, string] => e != null),
        );
        setRemotePaletteTileUrls(next);
        setRemotePaletteLoading(false);
      })();
    });

    return () => {
      cancelled = true;
    };
  }, [
    remoteSpectralIndex,
    remotePaletteCsv,
    defaultSpectralPalette,
    remoteShowSatelliteRaster,
    alphaEarthPreviewGeometryKey,
    floodPoint.exportGeometry,
    floodPoint.id,
    remoteSceneSlidesForFetch,
    paletteRetryToken,
  ]);

  /** Lazy-fetch chỉ số / profile RGB đang chọn (palette tùy chỉnh do effect riêng xử lý). */
  useEffect(() => {
    if (remoteSpectralIndex !== 'RGB' && remotePaletteCsv !== defaultSpectralPalette) return;
    if (!remoteShowSatelliteRaster) return;
    const remoteSceneSlides = remoteSceneSlidesForFetch;
    if (
      !env.SAR_RESULT_ALPHAEARTH_PREVIEW ||
      !floodPoint.exportGeometry ||
      !remoteSceneSlides?.length
    )
      return;
    const tile = resolveSarPreviewBboxAndTileXY(floodPoint.exportGeometry);
    if (!tile) return;

    const spec = galleryFetchSpecFromUi(remoteSpectralIndex, remoteRgbProfile);
    const specKey = geeFrameKeyFromSpec(spec);
    let cancelled = false;

    void (async () => {
      const datedSlides = remoteSceneSlides.filter(
        (s): s is AoiRemoteSceneSlide & { referenceDate: string } =>
          Boolean(s.referenceDate?.trim()),
      );
      for (const slide of datedSlides) {
        if (cancelled) return;
        const sk = getRemoteSceneSlideKey(slide);
        const existing = (slide.geeFrames ?? [])
          .concat(lazyFramesBySlide[sk] ?? [])
          .some((f) => geeFrameKeyFromFrame(f) === specKey && f.imageUrl?.trim());
        if (existing) continue;

        setInflightSpecsBySlide((prev) => {
          const cur = new Set(prev[sk] ?? []);
          cur.add(specKey);
          return { ...prev, [sk]: cur };
        });
        setFailedSpecsBySlide((prev) => {
          if (!prev[sk]?.has(specKey)) return prev;
          const cur = new Set(prev[sk]);
          cur.delete(specKey);
          return { ...prev, [sk]: cur };
        });

        const frame = await fetchSarGalleryFrame(tile.bbox, tile.x, tile.y, slide, spec);
        if (cancelled) return;

        setInflightSpecsBySlide((prev) => {
          if (!prev[sk]?.has(specKey)) return prev;
          const cur = new Set(prev[sk]);
          cur.delete(specKey);
          return { ...prev, [sk]: cur };
        });

        if (!frame?.imageUrl?.trim()) {
          setFailedSpecsBySlide((prev) => {
            const cur = new Set(prev[sk] ?? []);
            cur.add(specKey);
            return { ...prev, [sk]: cur };
          });
          continue;
        }

        setLazyFramesBySlide((prev) => {
          const cur = prev[sk] ?? [];
          const base = slide.geeFrames ?? [];
          const keys = new Set(
            [...base, ...cur].filter((f) => f.imageUrl?.trim()).map(geeFrameKeyFromFrame),
          );
          if (keys.has(geeFrameKeyFromFrame(frame))) return prev;
          return { ...prev, [sk]: [...cur, frame] };
        });
      }
    })();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- bỏ qua lazyFramesBySlide (đọc/ ghi trong effect, thêm deps gây lặp fetch)
  }, [
    remoteSpectralIndex,
    remoteRgbProfile,
    remotePaletteCsv,
    defaultSpectralPalette,
    remoteShowSatelliteRaster,
    alphaEarthPreviewGeometryKey,
    floodPoint.exportGeometry,
    floodPoint.id,
    remoteSceneSlidesForFetch,
    lazyRetryToken,
  ]);

  /** Prefetch nền các chỉ số còn lại khi tab Viễn thám mở (ưu tiên thấp). */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (effectiveTab !== 'remote') return;
    if (!remoteShowSatelliteRaster) return;
    const remoteSceneSlidesPrefetch = remoteSceneSlidesForFetch;
    if (
      !env.SAR_RESULT_ALPHAEARTH_PREVIEW ||
      !floodPoint.exportGeometry ||
      !remoteSceneSlidesPrefetch?.length
    )
      return;
    const tile = resolveSarPreviewBboxAndTileXY(floodPoint.exportGeometry);
    if (!tile) return;

    const genKey = `${floodPoint.id}:${remoteSceneSlidesPrefetch.length}`;
    if (remoteIdlePrefetchKeyRef.current === genKey) return;
    remoteIdlePrefetchKeyRef.current = genKey;

    const runPrefetch = () => {
      void (async () => {
        const slides = remoteSceneSlidesPrefetch.filter(
          (s): s is AoiRemoteSceneSlide & { referenceDate: string } =>
            Boolean(s.referenceDate?.trim()),
        );
        const uiSpec = galleryFetchSpecFromUi(remoteSpectralIndex, remoteRgbProfile);
        const orderedSpecs = [
          uiSpec,
          ...ALL_GALLERY_FETCH_SPECS.filter((s) => !gallerySpecsEqual(s, uiSpec)),
        ];
        for (const spec of orderedSpecs) {
          for (const slide of slides) {
            const sk = getRemoteSceneSlideKey(slide);
            const want = geeFrameKeyFromSpec(spec);
            const frame = await fetchSarGalleryFrame(tile.bbox, tile.x, tile.y, slide, spec);
            if (!frame?.imageUrl?.trim()) continue;
            setLazyFramesBySlide((prev) => {
              const cur = prev[sk] ?? [];
              const base = slide.geeFrames ?? [];
              const keys = new Set(
                [...base, ...cur].filter((f) => f.imageUrl?.trim()).map(geeFrameKeyFromFrame),
              );
              if (keys.has(want)) return prev;
              return { ...prev, [sk]: [...cur, frame] };
            });
          }
        }
      })();
    };

    const useRic = typeof window !== 'undefined' && 'requestIdleCallback' in window;
    const idleId = useRic
      ? window.requestIdleCallback(runPrefetch, { timeout: 4500 })
      : (window.setTimeout(runPrefetch, 500) as unknown as number);

    return () => {
      if (useRic) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, [
    effectiveTab,
    remoteShowSatelliteRaster,
    remoteSpectralIndex,
    remoteRgbProfile,
    floodPoint.id,
    floodPoint.exportGeometry,
    remoteSceneSlidesForFetch,
    alphaEarthPreviewGeometryKey,
  ]);

  const tabItems = useMemo(() => {
    const items: { key: TabKey; label: React.ReactNode }[] = [
      {
        key: 'overview',
        label: (
          <span className="flex items-center gap-1 text-[11px]">
            <LayoutGrid size={12} className="opacity-80" />
            {t('flood.aoiInsight.tabOverview')}
          </span>
        ),
      },
      {
        key: 'impact',
        label: (
          <span className="flex items-center gap-1 text-[11px]">
            <Building2 size={12} className="opacity-80" />
            {t('flood.aoiInsight.tabImpact')}
          </span>
        ),
      },
      {
        key: 'remote',
        label: (
          <span className="flex items-center gap-1 text-[11px]">
            <Satellite size={12} className="opacity-80" />
            {t('flood.aoiInsight.tabRemote')}
          </span>
        ),
      },
    ];
    if (showHistoryTab) {
      items.push({
        key: 'history',
        label: (
          <span className="flex items-center gap-1 text-[11px]">
            <History size={12} className="opacity-80" />
            {t('flood.history.title', { defaultValue: 'Lịch sử' })}
          </span>
        ),
      });
    }
    if (!hideActionsTab) {
      items.push({
        key: 'actions',
        label: (
          <span className="flex items-center gap-1 text-[11px]">
            <ShieldAlert size={12} className="opacity-80" />
            {t('flood.safety.emergencyContacts')}
          </span>
        ),
      });
    }
    return items;
  }, [t, showHistoryTab, hideActionsTab]);

  const sectionLabelClass =
    'mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400';

  const renderOverview = () => {
    const haDisplay = ctx
      ? `${ctx.floodedAreaHa} ha`
      : `${(Math.round(floodPoint.areaKm2 * 100 * 100) / 100).toFixed(2)} ha`;
    const depthRange =
      ctx != null
        ? `${ctx.estimatedDepthMinM} – ${ctx.estimatedDepthMaxM} m`
        : `${(floodPoint.depthCm / 100).toFixed(2)} m`;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <StatBox
            icon={<LayoutGrid size={16} className="text-emerald-600 dark:text-emerald-400" />}
            label={t('flood.aoiInsight.floodedAreaHa')}
            value={haDisplay}
          />
          <StatBox
            icon={<MapPin size={16} className="text-sky-600 dark:text-sky-400" />}
            label={t('flood.aoiInsight.floodedAreaKm2')}
            value={`${floodPoint.areaKm2} km²`}
          />
          <StatBox
            icon={<Calendar size={16} className="text-amber-400" />}
            label={t('flood.aoiInsight.depthEstimate')}
            value={depthRange}
          />
          <StatBox
            icon={<Calendar size={16} className="text-cyan-600 dark:text-cyan-400" />}
            label={t('flood.aoiInsight.depthAtPoint')}
            value={`${floodPoint.depthCm} cm`}
          />
        </div>

        {ctx != null ? (
          <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] p-3">
            <Tooltip title={t('flood.aoiInsight.affectedShareTooltip')} placement="topLeft">
              <div className="inline-flex cursor-help border-b border-dotted border-slate-500/45">
                <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                  {t('flood.aoiInsight.affectedShare')}
                </span>
              </div>
            </Tooltip>
            <div className="mt-1 text-lg font-bold tabular-nums text-slate-900 dark:text-slate-50">
              {ctx.affectedAreaPercent}%
            </div>
            <div className="text-[11px] text-slate-500">
              {t('flood.aoiInsight.affectedShareHint', { unit: ctx.administrativeUnitLabel })}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <StatBox
            icon={<ShieldAlert size={16} className="text-orange-600 dark:text-orange-400" />}
            label={t('flood.aoiInsight.modelConfidence')}
            value={`${floodPoint.confidence}%`}
          />
          <StatBox
            icon={<Building2 size={16} className="text-slate-400" />}
            label={t('flood.population.total')}
            value={floodPoint.population.toLocaleString(localeTag)}
          />
        </div>
      </div>
    );
  };

  const renderImpact = () => (
    <div className="space-y-5">
      {ctx != null && ctx.landCoverImpacts.length > 0 ? (
        <div>
          <div className={sectionLabelClass}>{t('flood.aoiInsight.landCoverTitle')}</div>
          <ul className="space-y-3">
            {ctx.landCoverImpacts.map((row, idx) => {
              const dist = landCoverDistributionRows.find((d) => d.categoryKey === row.categoryKey);
              return (
                <li
                  key={`${row.categoryKey}-${idx}`}
                  className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] px-3 py-2.5"
                >
                  <LandCoverPercentBarRow
                    categoryKey={row.categoryKey}
                    label={t(landCoverLabelKey(row.categoryKey))}
                    areaHa={row.areaHa}
                    pctOfTotal={dist?.pctOfTotal ?? null}
                    compact
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <div>
        <div className={sectionLabelClass}>{t('flood.aoiInsight.infrastructureTitle')}</div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {(
            [
              ['hospitals', floodPoint.nearbyInfrastructure.hospitals],
              ['schools', floodPoint.nearbyInfrastructure.schools],
              ['emergencyShelters', floodPoint.nearbyInfrastructure.emergencyShelters],
              ['fireStations', floodPoint.nearbyInfrastructure.fireStations],
              ['policeStations', floodPoint.nearbyInfrastructure.policeStations],
              ['evacuationCenters', floodPoint.nearbyInfrastructure.evacuationCenters],
            ] as const
          ).map(([key, n]) => (
            <div
              key={key}
              className="rounded-md border border-[var(--glass-border)] bg-[var(--bg-card)] px-2 py-1.5"
            >
              <span className="text-slate-500">{t(`flood.infrastructure.${key}`)}</span>
              <span className="ml-1 font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {n}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className={sectionLabelClass}>{t('flood.aoiInsight.roadsTitle')}</div>
        <ul className="space-y-2">
          {floodPoint.blockedRoads.slice(0, 5).map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] px-3 py-2 text-[11px] text-slate-700 dark:text-slate-200"
            >
              <span className="font-medium">{r.name}</span>
              <span className="text-slate-500"> · {r.severity}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className={sectionLabelClass}>{t('flood.aoiInsight.damageTitle')}</div>
        <div className="space-y-1.5 text-[11px] text-slate-600 dark:text-slate-300">
          <div>
            {t('flood.damage.affectedHouses')}:{' '}
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {floodPoint.damageAssessment.affectedHouses}
            </span>
          </div>
          <div>
            {t('flood.damage.economicLoss')}:{' '}
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {new Intl.NumberFormat(localeTag, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(floodPoint.damageAssessment.economicLossUSD)}
            </span>
          </div>
          <div>
            {t('flood.damage.agriculturalArea')}:{' '}
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {floodPoint.damageAssessment.agriculturalAreaHa} ha
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRemote = () => (
    <div className="space-y-4">
      {ctx != null ? (
        <>
          <div>
            <Badge
              status={floodPoint.verified ? 'success' : 'default'}
              text={
                <span className="text-xs">
                  {floodPoint.verified
                    ? t('flood.aoiInsight.verified')
                    : t('flood.aoiInsight.notVerified')}
                </span>
              }
            />
          </div>
          {(ctx.remoteSceneSlides != null && ctx.remoteSceneSlides.length > 0) ||
          (enableScrub && scrubReady && ctx?.remoteTimeWindows) ? (
            <div className="space-y-3">
              <div>
                <div className={sectionLabelClass}>
                  {t('flood.aoiInsight.remoteSceneGalleryTitle')}
                </div>
                <p className="mb-2 text-[10px] leading-snug text-slate-500 dark:text-slate-400">
                  {t('flood.aoiInsight.remoteSceneToolbarHint')}
                </p>
                <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
                  <label className="inline-flex cursor-pointer select-none items-center gap-2 text-[10px] text-slate-600 dark:text-slate-300">
                    <Switch
                      size="small"
                      checked={remoteShowSatelliteRaster}
                      onChange={setRemoteShowSatelliteRaster}
                    />
                    {t('flood.aoiInsight.remoteShowSatelliteRaster')}
                  </label>
                </div>
                <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] p-2 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
                    <div className="min-w-0 flex-1 space-y-1">
                      <label className="block text-[10px] font-medium text-slate-600 dark:text-slate-300">
                        {t('flood.aoiInsight.remoteSceneIndexSelect')}
                      </label>
                      <Select<AlphaEarthIndex>
                        size="middle"
                        className="w-full [&_.ant-select-selector]:!min-h-8 [&_.ant-select-selector]:!rounded-md [&_.ant-select-selection-item]:!text-[12px]"
                        popupMatchSelectWidth={false}
                        value={remoteSpectralIndex}
                        onChange={handleRemoteSpectralIndexChange}
                        options={SAR_SCENE_GALLERY_INDICES.map((idx) => ({
                          value: idx,
                          label: t(`flood.aoiInsight.geeIndex.${idx}`),
                        }))}
                      />
                    </div>
                    {remoteSpectralIndex !== 'RGB' && spectralPalettePresets.length > 0 ? (
                      <div className="min-w-0 flex-1 space-y-1 sm:border-l sm:border-[var(--glass-border)] sm:pl-3">
                        <label className="block text-[10px] font-medium text-slate-600 dark:text-slate-300">
                          {t('flood.aoiInsight.remoteScenePaletteSelect')}
                        </label>
                        <Select<string>
                          size="middle"
                          className="w-full [&_.ant-select-selector]:!min-h-8 [&_.ant-select-selector]:!rounded-md [&_.ant-select-selection-item]:!text-[12px]"
                          value={remotePaletteCsv}
                          onChange={(v) => setRemotePaletteCsv(v)}
                          disabled={
                            spectralPalettePresets.length <= 1 ||
                            !env.SAR_RESULT_ALPHAEARTH_PREVIEW ||
                            !floodPoint.exportGeometry
                          }
                          options={spectralPalettePresets.map((p) => ({
                            value: p.palette,
                            label: t(p.labelKey),
                          }))}
                        />
                        {remotePaletteLoading ? (
                          <span className="text-[10px] text-slate-500">
                            {t('flood.aoiInsight.remoteScenePaletteLoading')}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div
                className={`grid gap-4 ${remoteScenePreviewColumns.length > 1 ? 'sm:grid-cols-2' : ''}`}
              >
                {remoteScenePreviewColumns.map(({ role, displaySlide, fetchSlide }) => {
                  const frames = remoteGeeFramesForSlide(fetchSlide, lazyFramesBySlide);
                  const frame = pickRemoteFrame(frames, remoteSpectralIndex, remoteRgbProfile);
                  const slideKey = getRemoteSceneSlideKey(fetchSlide);
                  const uiSpec = galleryFetchSpecFromUi(remoteSpectralIndex, remoteRgbProfile);
                  const uiSpecKey = geeFrameKeyFromSpec(uiSpec);
                  const paletteOverrideMode =
                    remoteSpectralIndex !== 'RGB' &&
                    remotePaletteCsv !== defaultSpectralPalette &&
                    Boolean(fetchSlide.referenceDate);
                  const paletteStatusKey = fetchSlide.referenceDate
                    ? `${fetchSlide.role}-${fetchSlide.referenceDate}`
                    : '';
                  const paletteOverride = paletteOverrideMode
                    ? remotePaletteTileUrls[paletteStatusKey]
                    : undefined;
                  const displayImageUrl = paletteOverride?.trim() || frame?.imageUrl?.trim();
                  const scrubRowPending =
                    enableScrub &&
                    (role === 'baseline'
                      ? debouncedBaselineIso == null || scrubBaselineIso !== debouncedBaselineIso
                      : debouncedCrisisIso == null || scrubCrisisIso !== debouncedCrisisIso);
                  const isTileLoading =
                    !displayImageUrl &&
                    (paletteOverrideMode
                      ? remotePaletteStatus[paletteStatusKey] === 'loading'
                      : (inflightSpecsBySlide[slideKey]?.has(uiSpecKey) ?? false) ||
                        (enableScrub && scrubRowPending && env.SAR_RESULT_ALPHAEARTH_PREVIEW));
                  const isTileError =
                    !displayImageUrl &&
                    !isTileLoading &&
                    (paletteOverrideMode
                      ? remotePaletteStatus[paletteStatusKey] === 'error'
                      : (failedSpecsBySlide[slideKey]?.has(uiSpecKey) ?? false));
                  /** Chỉ hiển thị ngày / khoảng tháng tới người dùng (không kèm RGB, embedding, tên chỉ số kỹ thuật). */
                  const remoteCaptureFooterText =
                    displaySlide.referenceDate != null && displaySlide.referenceDate.trim() !== ''
                      ? formatDate(`${displaySlide.referenceDate}T12:00:00`)
                      : displaySlide.periodLabel;
                  const previewMapHeight = spaciousRemoteGallery
                    ? remoteScenePreviewColumns.length > 1
                      ? 'min(50vh, 540px)'
                      : 'min(56vh, 620px)'
                    : remoteScenePreviewColumns.length > 1
                      ? 'min(36vh, 340px)'
                      : 'min(42vh, 400px)';
                  const previewFallbackMaxPx = spaciousRemoteGallery
                    ? remoteScenePreviewColumns.length > 1
                      ? 540
                      : 620
                    : remoteScenePreviewColumns.length > 1
                      ? 340
                      : 400;
                  const jobMaxDepthM = Math.max(0, floodPoint.maxDepthCm) / 100;
                  const scrubDepthForMap = enableScrub
                    ? scrubPreviewDepthMeters(
                        role,
                        role === 'baseline' ? scrubBaselineT01 : scrubCrisisT01,
                        jobMaxDepthM,
                      )
                    : undefined;
                  const useGeorefMap = Boolean(
                    displayImageUrl && floodPoint.exportGeometry && sarPreviewTileXY,
                  );
                  return (
                    <div key={role} className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-md bg-slate-200/80 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-slate-800 dark:bg-slate-600/40 dark:text-slate-100">
                          {remoteColumnRoleShortLabel(t, displaySlide.role)}
                        </span>
                        <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                          {displaySlide.periodLabel}
                        </span>
                        {enableScrub && scrubRowPending ? (
                          <span className="text-[10px] text-sky-600 dark:text-sky-400/90">
                            {t('flood.aoiInsight.remoteScrubberPending')}
                          </span>
                        ) : null}
                      </div>
                      <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-1.5 shadow-sm">
                        {displayImageUrl ? (
                          <div className="space-y-1.5">
                            {useGeorefMap && floodPoint.exportGeometry && sarPreviewTileXY ? (
                              <RemoteSceneGeoreferencedPreview
                                mapInstanceId={`${floodPoint.id}-inline-${role}`}
                                imageUrl={displayImageUrl}
                                aoiPolygon={floodPoint.exportGeometry}
                                tileX={sarPreviewTileXY.x}
                                tileY={sarPreviewTileXY.y}
                                mapHeight={previewMapHeight}
                                showSatelliteRaster={remoteShowSatelliteRaster}
                                expandAriaLabel={t('flood.aoiInsight.remoteSceneExpand')}
                                floodExtentGeometry={
                                  role === 'crisis'
                                    ? enableScrub
                                      ? (scrubCrisisFloodGeometry ?? undefined)
                                      : (ctx?.sarFloodExtentGeometry ?? undefined)
                                    : enableScrub
                                      ? (scrubBaselineFloodGeometry ?? undefined)
                                      : undefined
                                }
                                floodNominalDepthM={scrubDepthForMap}
                                onRequestExpand={() =>
                                  setRemotePreviewModal({
                                    role,
                                    title: `${remoteColumnRoleShortLabel(t, displaySlide.role)} — ${remoteCaptureFooterText}`,
                                    imageUrl: displayImageUrl,
                                  })
                                }
                              />
                            ) : !remoteShowSatelliteRaster ? (
                              <div
                                className="flex min-h-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-[var(--glass-border)] bg-[#0b1220] px-3 py-6 text-center"
                                style={{ maxHeight: previewFallbackMaxPx }}
                              >
                                <span className="text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                                  {t('flood.aoiInsight.remoteRasterOffPlaceholder')}
                                </span>
                              </div>
                            ) : (
                              <Image
                                src={displayImageUrl}
                                alt={t('flood.aoiInsight.remoteSceneCaptureLine', {
                                  date: remoteCaptureFooterText,
                                })}
                                className="rounded-lg border border-[var(--glass-border)]"
                                style={{
                                  width: '100%',
                                  maxHeight: previewFallbackMaxPx,
                                  objectFit: 'cover',
                                }}
                                styles={{
                                  popup: {
                                    root: { padding: 16 },
                                    body: { padding: 20, background: 'rgba(0,0,0,0.88)' },
                                  },
                                }}
                                preview={{ zIndex: 3100 }}
                              />
                            )}
                            <div className="px-1 pb-0.5 pt-0.5 text-center">
                              <p className="!mb-0 text-[11px] leading-snug text-slate-600 dark:text-slate-300">
                                {t('flood.aoiInsight.remoteSceneCaptureLine', {
                                  date: remoteCaptureFooterText,
                                })}
                              </p>
                            </div>
                          </div>
                        ) : isTileLoading ? (
                          <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 py-6">
                            <Spin />
                            <span className="text-[11px] text-slate-500">
                              {t(
                                paletteOverrideMode
                                  ? 'flood.aoiInsight.remoteScenePaletteLoading'
                                  : 'flood.aoiInsight.remoteSceneTileLoading',
                              )}
                            </span>
                          </div>
                        ) : isTileError ? (
                          <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 py-6 text-center">
                            <AlertCircle
                              size={22}
                              className="text-amber-500 dark:text-amber-400"
                              aria-hidden
                            />
                            <span className="max-w-[220px] text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                              {t('flood.aoiInsight.remoteSceneTileError')}
                            </span>
                            <Button
                              size="small"
                              type="default"
                              icon={<RotateCw size={12} />}
                              onClick={() => {
                                if (paletteOverrideMode) {
                                  setRemotePaletteStatus((prev) => {
                                    if (!prev[paletteStatusKey]) return prev;
                                    const next = { ...prev };
                                    delete next[paletteStatusKey];
                                    return next;
                                  });
                                  setPaletteRetryToken((n) => n + 1);
                                } else {
                                  setFailedSpecsBySlide((prev) => {
                                    if (!prev[slideKey]?.has(uiSpecKey)) return prev;
                                    const cur = new Set(prev[slideKey]);
                                    cur.delete(uiSpecKey);
                                    return { ...prev, [slideKey]: cur };
                                  });
                                  setLazyRetryToken((n) => n + 1);
                                }
                              }}
                            >
                              {t('flood.aoiInsight.remoteSceneTileRetry')}
                            </Button>
                          </div>
                        ) : ctx.remoteSceneGalleryLoading ? (
                          <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 py-6">
                            <Spin />
                            <span className="text-[11px] text-slate-500">
                              {t('flood.aoiInsight.remoteSceneGalleryLoading')}
                            </span>
                          </div>
                        ) : floodPoint.exportGeometry && sarPreviewTileXY ? (
                          <div className="space-y-1.5">
                            <RemoteSceneGeoreferencedPreview
                              mapInstanceId={`${floodPoint.id}-inline-${role}-fallback`}
                              imageUrl=""
                              aoiPolygon={floodPoint.exportGeometry}
                              tileX={sarPreviewTileXY.x}
                              tileY={sarPreviewTileXY.y}
                              mapHeight={previewMapHeight}
                              showSatelliteRaster={false}
                              floodExtentGeometry={
                                role === 'crisis'
                                  ? enableScrub
                                    ? (scrubCrisisFloodGeometry ?? undefined)
                                    : (ctx?.sarFloodExtentGeometry ?? undefined)
                                  : enableScrub
                                    ? (scrubBaselineFloodGeometry ?? undefined)
                                    : undefined
                              }
                              floodNominalDepthM={scrubDepthForMap}
                            />
                            <div className="px-1 pb-0.5 pt-0.5 text-center">
                              <p className="!mb-0 text-[11px] leading-snug text-slate-600 dark:text-slate-300">
                                {t('flood.aoiInsight.remoteSceneCaptureLine', {
                                  date: remoteCaptureFooterText,
                                })}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <Empty
                            className="py-6"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                              <span className="text-[11px] text-slate-500">
                                {t('flood.aoiInsight.remoteSceneNoGee')}
                              </span>
                            }
                          />
                        )}
                      </div>
                      {enableScrub && scrubReady && ctx?.remoteTimeWindows ? (
                        <RemoteSceneColumnTimeScrubber
                          timeWindow={
                            role === 'baseline'
                              ? ctx.remoteTimeWindows.baseline
                              : ctx.remoteTimeWindows.crisis
                          }
                          t01={role === 'baseline' ? scrubBaselineT01 : scrubCrisisT01}
                          onT01Change={
                            role === 'baseline' ? setScrubBaselineT01 : setScrubCrisisT01
                          }
                          onIsoChange={
                            role === 'baseline' ? setScrubBaselineIso : setScrubCrisisIso
                          }
                          formatDay={formatDateYmd}
                          ariaLabel={
                            role === 'baseline'
                              ? t('flood.aoiInsight.remoteScrubberReferenceTitle')
                              : t('flood.aoiInsight.remoteScrubberCompareTitle')
                          }
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] px-2 py-6">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span className="max-w-xs text-center text-[11px] text-slate-500">
                    {t('flood.aoiInsight.remoteSceneNoStrip')}
                  </span>
                }
              />
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2 text-[11px] text-slate-600 dark:text-slate-300">
          <div>
            <span className="text-slate-500">{t('flood.aoiInsight.modelConfidence')}: </span>
            <span className="font-semibold">{floodPoint.confidence}%</span>
          </div>
          <Badge
            status={floodPoint.verified ? 'success' : 'default'}
            text={
              <span className="text-xs">
                {floodPoint.verified
                  ? t('flood.aoiInsight.verified')
                  : t('flood.aoiInsight.notVerified')}
              </span>
            }
          />
        </div>
      )}
    </div>
  );

  const renderLandCover = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-slate-100 pb-2 dark:border-slate-800">
        <Satellite size={18} className="text-sky-500" />
        <h3 className="!mb-0 text-sm font-bold text-slate-900 dark:text-white">
          {t('flood.aoiInsight.landCoverTitle')}
        </h3>
      </div>
      {ctx?.vegetationCoverPct != null && (
        <div className="grid grid-cols-2 gap-3">
          <StatBox
            icon={<Satellite size={16} className="text-emerald-600 dark:text-emerald-400" />}
            label={t('flood.aoiInsight.vegetationCover')}
            value={`${ctx.vegetationCoverPct}%`}
          />
        </div>
      )}
      <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
        {t('flood.aoiInsight.landCoverTabIntro')}
      </p>
      <SarAoiLandCoverMapTab
        aoi={floodPoint.exportGeometry}
        landImpacts={ctx?.landCoverImpacts}
        dateRangeMinIso={landCoverDateRange?.minIso}
        dateRangeMaxIso={landCoverDateRange?.maxIso}
      />
    </div>
  );

  const renderHistory = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3">
        <StatBox
          icon={<Calendar size={16} className="text-blue-600 dark:text-blue-400" />}
          label={t('flood.history.totalEvents')}
          value={t('flood.history.eventCountValue', { count: floodPoint.history.length })}
        />
      </div>

      <div>
        <div className={sectionLabelClass}>
          {t('flood.history.title', { defaultValue: 'Lịch sử ngập' })}
        </div>
        <Timeline
          items={floodPoint.history.slice(0, 10).map((record) => ({
            color:
              record.damageLevel === 'severe'
                ? 'red'
                : record.damageLevel === 'moderate'
                  ? 'orange'
                  : 'blue',
            children: (
              <div className="pb-2">
                <span className="text-sm font-medium text-slate-900 dark:text-white">
                  {formatDate(record.date)}
                </span>
              </div>
            ),
          }))}
        />
      </div>
    </div>
  );

  const renderActions = () => (
    <div className="space-y-5">
      <div>
        <div className={sectionLabelClass}>{t('flood.safety.emergencyContacts')}</div>
        <div className="space-y-2">
          {floodPoint.safetyRecommendations.emergencyContacts.map((contact, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] p-3"
            >
              <div>
                <div className="text-sm font-medium text-slate-900 dark:text-white">
                  {contact.name}
                </div>
                <div className="text-xs text-slate-400">{contact.role}</div>
              </div>
              <div className="flex items-center gap-2">
                {contact.available ? (
                  <Badge
                    status="success"
                    text={
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        {t('common.online')}
                      </span>
                    }
                  />
                ) : (
                  <Badge
                    status="error"
                    text={<span className="text-xs text-red-400">{t('common.offline')}</span>}
                  />
                )}
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center gap-1 text-sm font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
                >
                  <Phone size={14} />
                  {contact.phone}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {!landCoverOnly ? (
        <div className="border-b border-[var(--glass-border)] bg-[var(--mdm-header-tint)] dark:bg-slate-950/25">
          <Tabs
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as TabKey)}
            items={tabItems}
            size="small"
            className="flood-popup-tabs px-1"
          />
        </div>
      ) : null}

      <div
        className="custom-scrollbar overflow-y-auto p-3.5"
        style={{ maxHeight: contentMaxHeight }}
      >
        {landCoverOnly ? (
          renderLandCover()
        ) : (
          <>
            {effectiveTab === 'overview' && renderOverview()}
            {effectiveTab === 'impact' && renderImpact()}
            {effectiveTab === 'remote' && renderRemote()}
            {effectiveTab === 'history' && renderHistory()}
            {effectiveTab === 'actions' && renderActions()}
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-[var(--glass-border)] bg-[var(--mdm-header-tint)] px-3 py-2 dark:bg-slate-950/30">
        <Button
          type="default"
          size="small"
          icon={<Download size={14} />}
          onClick={handleExportGeoJson}
        >
          {t('flood.aoiInsight.exportGeojson')}
        </Button>
      </div>

      {showLastUpdatedFooter ? (
        <div className="flex items-center border-t border-[var(--glass-border)] bg-[var(--mdm-header-tint)] px-3 py-2.5 text-[11px] text-slate-500 dark:bg-slate-950/30 dark:text-slate-400">
          <Clock size={12} className="shrink-0" />
          <span className="ml-2">
            {t('flood.lastUpdated', { defaultValue: 'Cập nhật' })}:{' '}
            {formatTime(floodPoint.lastUpdated)}
          </span>
        </div>
      ) : null}

      <Modal
        open={remotePreviewModal != null}
        title={remotePreviewModal?.title}
        onCancel={() => setRemotePreviewModal(null)}
        footer={null}
        width={spaciousRemoteGallery ? 'min(1200px, 99vw)' : 'min(960px, 96vw)'}
        centered
        destroyOnHidden
        styles={{ body: { padding: 12 } }}
      >
        {remotePreviewModal && sarPreviewTileXY && floodPoint.exportGeometry ? (
          <RemoteSceneGeoreferencedPreview
            mapInstanceId={`${floodPoint.id}-zoom-${remotePreviewModal.role}`}
            imageUrl={remotePreviewModal.imageUrl}
            aoiPolygon={floodPoint.exportGeometry}
            tileX={sarPreviewTileXY.x}
            tileY={sarPreviewTileXY.y}
            mapHeight={spaciousRemoteGallery ? 'min(86vh, 900px)' : 'min(78vh, 680px)'}
            showSatelliteRaster={remoteShowSatelliteRaster}
            floodExtentGeometry={
              remotePreviewModal.role === 'crisis'
                ? enableScrub
                  ? (scrubCrisisFloodGeometry ?? undefined)
                  : (ctx?.sarFloodExtentGeometry ?? undefined)
                : enableScrub
                  ? (scrubBaselineFloodGeometry ?? undefined)
                  : undefined
            }
            floodNominalDepthM={
              enableScrub
                ? scrubPreviewDepthMeters(
                    remotePreviewModal.role,
                    remotePreviewModal.role === 'baseline' ? scrubBaselineT01 : scrubCrisisT01,
                    Math.max(0, floodPoint.maxDepthCm) / 100,
                  )
                : undefined
            }
          />
        ) : null}
      </Modal>
    </div>
  );
}

export function EnhancedFloodPointInsightPanel(props: EnhancedFloodPointInsightPanelProps) {
  return <EnhancedFloodPointInsightPanelInner key={props.floodPoint.id} {...props} />;
}
